import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Flex,
    Stack,
} from "@chakra-ui/react";
import {
    FaEdit,
    FaTrashAlt
} from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import Loading from "../../../components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { personsService } from "services/personsService";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { PersonFilter } from "./PersonFilter";
import { ROUTES } from "../../../constants/routes-path";
import { PERSON_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

function PersonsList() {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(PERSON_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const dialogRef = useRef(undefined);
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const [personToDelete, setPersonToDelete] = useState(null);

    const columns = useMemo(() => [
        {
            Header: t("settings.person.list.table.header.cpfcnpj"),
            accessor: "cpfcnpj",
            id: "cpfCnpj"
        },
        {
            Header: t("settings.person.list.table.header.name"),
            accessor: "nome",
        },
        {
            Header: t("settings.person.list.table.header.social-name"),
            accessor: "nome_social",
        },
        {
            Header: t("settings.person.list.table.header.address"),
            accessor: "endereco_logradouro",
        },
        {
            Header: t("settings.person.list.table.header.telephone"),
            accessor: "telefone",
        },
        {
            Header: t("settings.person.list.table.header.cellphone"),
            accessor: "celular",
        },
        {
            Header: t("settings.person.list.table.header.email"),
            accessor: "email",
        },
        {
            Header: t("settings.person.list.table.header.photo"),
            disableSortBy: true,
            accessor: "foto",
        },
        {
            Header: t("settings.person.list.table.header.creation-date"),
            accessor: "data_criacao",
        },
        {
            Header: t("settings.person.list.table.header.actions"),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => navigate(ROUTES.personEditPath.replace(":id", row.original.id)),
                    icon: <FaEdit />
                },
                {
                    label: t('system.actions.remove'),
                    onClick: (row) => { handleRemove(row.original.id) },
                    icon: <FaTrashAlt color="#E53E3E" />,
                }
            ],

        }
    ], []);

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "cpfCnpj";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await personsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const clearPersonFilters = () => {
        fetchData({
            page: 1,
            filters: { cpfCnpj: null, name: null, email: null },
        });
    };

    const handleRemove = (personId) => {
        setPersonToDelete(personId);
        dialogRef.current?.displayDialog({
            title: t("settings.person.list.actions.remove-person"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmRemove = async () => {
        if (personToDelete) {
            try {
                await personsService.delete(personToDelete);
                showToast({
                    title: t("settings.person.messages.success.delete"),
                    status: "success",
                });
                await fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", sm: "125px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Button
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    onClick={() => navigate(ROUTES.personAddPath)}
                    alignSelf={{ sm: 'flex-start', lg: null }}
                >
                    {t("settings.person.new.button")}
                </Button>
                <Stack
                    direction='row'
                    pt={{ sm: "20px", md: "0",  lg: "0" }}
                    spacing='10px'
                    alignSelf={{ sm: 'flex-start', lg: 'auto' }}
                >
                    <PersonFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => fetchData({ filters })}
                    />
                </Stack>
            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("settings.person.title")} subtitle={t("settings.person.description")} />
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearPersonFilters} />
                    {response ? (
                        <SearchTable2
                            columnsData={columns}
                            tableData={response.content}
                            pageableData={toTablePageableData(response, { defaultField: "cpfcnpj", ...searchParams })}
                            onChange={fetchData}
                            disableSearch={true}
                        />
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default PersonsList;